import { useEffect, useCallback } from 'react';

/**
 * Custom hook for smooth scrolling to position an element near the top of the viewport.
 * Duration is proportional to distance (up to 2 seconds max).
 *
 * @param {Object} options
 * @param {React.RefObject} options.targetRef - Primary element to scroll to
 * @param {React.RefObject} [options.fallbackRef] - Fallback element if targetRef is empty
 * @param {boolean} [options.autoScroll=false] - If true, scrolls on mount
 * @param {number} [options.topOffset=10] - Pixels from viewport top to position element
 * @returns {{ scrollToTarget: () => void }} - Function to trigger scroll manually
 */
export function useSmartScroll({ targetRef, fallbackRef, autoScroll = false, topOffset = 10 }) {
  const scrollToTarget = useCallback(() => {
    const element = targetRef?.current || fallbackRef?.current;
    if (!element) return;

    const elementRect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const distance = elementRect.top - topOffset;

    // If already at target position (within 10px), no need to scroll
    if (Math.abs(distance) <= 10) return;

    // Duration proportional to distance, max 2 seconds
    const duration = Math.min((Math.abs(distance) / viewportHeight) * 2000, 2000);
    const startPosition = window.pageYOffset;
    const startTime = performance.now();

    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-in-out quadratic
      const easeProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      window.scrollTo(0, startPosition + (distance * easeProgress));

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }, [targetRef, fallbackRef, topOffset]);

  // Auto-scroll on mount if requested
  useEffect(() => {
    if (autoScroll) {
      scrollToTarget();
    }
  }, [autoScroll, scrollToTarget]);

  return { scrollToTarget };
}
